import { useState, useEffect, useLayoutEffect, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { useTokens } from '../context/ThemeContext';

// ── Storage helpers ────────────────────────────────────────────────────────────
const SEEN_KEY = 'nura_tour_seen_v1';
export const markTourSeen = (tourId) => {
  try {
    const seen = JSON.parse(localStorage.getItem(SEEN_KEY) || '{}');
    seen[tourId] = true;
    localStorage.setItem(SEEN_KEY, JSON.stringify(seen));
  } catch {}
};
export const isTourSeen = (tourId) => {
  try {
    const seen = JSON.parse(localStorage.getItem(SEEN_KEY) || '{}');
    return !!seen[tourId];
  } catch { return false; }
};

// ── Tour step definitions ──────────────────────────────────────────────────────
export const TOURS = {
  transactions: [
    {
      target: '[data-tour="add-btn"]',
      title: 'Add or import',
      body: 'Tap + to manually add an expense, or upload a bank statement (PDF or CSV) to import transactions in bulk.',
      placement: 'bottom',
    },
    {
      target: '[data-tour="search-btn"]',
      title: 'Search transactions',
      body: 'Tap the search icon to find transactions by name or category.',
      placement: 'bottom',
    },
    {
      target: '[data-tour="view-mode"]',
      title: 'Change the period',
      body: 'Switch between Daily, Weekly, Monthly, Quarterly, or Yearly views to see your spending over any time range.',
      placement: 'bottom',
    },
  ],
  analytics: [
    {
      target: '[data-tour="analytics-view-mode"]',
      title: 'Change the period',
      body: 'Switch time ranges to analyse your spending trends over different periods.',
      placement: 'bottom',
    },
    {
      target: '[data-tour="analytics-cards"]',
      title: 'Summary cards',
      body: 'See your total spend, income, net savings, and investments at a glance.',
      placement: 'bottom',
    },
    {
      target: '[data-tour="analytics-chart"]',
      title: 'Spending over time',
      body: 'This chart shows how your spending has changed month by month.',
      placement: 'top',
    },
    {
      target: '[data-tour="analytics-categories"]',
      title: 'Category breakdown',
      body: 'See which categories you spend the most on.',
      placement: 'top',
    },
  ],
  budgets: [
    {
      target: '[data-tour="new-budget-btn"]',
      title: 'Create a budget',
      body: 'Set a spending limit for any category — weekly, monthly, or yearly.',
      placement: 'bottom',
    },
    {
      target: '[data-tour="budget-list"]',
      title: 'Your budgets',
      body: 'Track how much you\'ve spent against each budget. Budgets turn amber at 80% and red when exceeded.',
      placement: 'top',
      fallback: true, // show centered if no budgets yet
    },
  ],
};

// ── Rect helper ────────────────────────────────────────────────────────────────
function getRect(selector) {
  if (!selector) return null;
  const el = document.querySelector(selector);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  return { top: r.top, left: r.left, width: r.width, height: r.height, bottom: r.bottom, right: r.right };
}

const PAD = 8; // spotlight padding around element

// ── Tooltip placement ──────────────────────────────────────────────────────────
function getTooltipStyle(rect, placement, tooltipW = 300) {
  if (!rect) return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };

  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const margin = 12;

  let top, left;

  if (placement === 'bottom') {
    top = rect.bottom + PAD + margin;
    left = rect.left + rect.width / 2 - tooltipW / 2;
  } else {
    top = rect.top - PAD - margin - 160; // rough tooltip height
    left = rect.left + rect.width / 2 - tooltipW / 2;
  }

  // Clamp horizontally
  left = Math.max(margin, Math.min(left, vw - tooltipW - margin));
  // Clamp vertically
  top = Math.max(margin, Math.min(top, vh - 180));

  return { top: `${top}px`, left: `${left}px`, width: `${tooltipW}px` };
}

// ── Arrow indicator pointing to target ────────────────────────────────────────
function getArrowStyle(rect, placement) {
  if (!rect) return null;
  const tipLeft = parseFloat(getTooltipStyle(rect, placement).left) || 0;
  const tipW = 300;
  const arrowLeft = rect.left + rect.width / 2 - tipLeft;
  const clamped = Math.max(16, Math.min(arrowLeft, tipW - 16));
  return { left: `${clamped}px` };
}

// ── Main component ─────────────────────────────────────────────────────────────
function OnboardingTour({ tourId, steps, active, onClose }) {
  const t = useTokens();
  const [step, setStep] = useState(0);
  const [rect, setRect] = useState(null);

  const currentStep = steps[step];

  const measureTarget = useCallback(() => {
    if (!currentStep) return;
    const r = getRect(currentStep.target);
    if (r) {
      setRect(r);
    } else if (currentStep.fallback) {
      setRect(null); // centered tooltip
    }
  }, [currentStep]);

  // Re-measure on step change and on resize/scroll
  useLayoutEffect(() => {
    if (!active) return;
    measureTarget();
    window.addEventListener('resize', measureTarget);
    window.addEventListener('scroll', measureTarget, true);
    return () => {
      window.removeEventListener('resize', measureTarget);
      window.removeEventListener('scroll', measureTarget, true);
    };
  }, [active, measureTarget]);

  // Retry measuring if target not found yet (e.g. view still mounting)
  useEffect(() => {
    if (!active || rect) return;
    const id = setInterval(() => {
      const r = getRect(currentStep?.target);
      if (r) { setRect(r); clearInterval(id); }
    }, 100);
    return () => clearInterval(id);
  }, [active, rect, currentStep]);

  // Reset step when tour opens
  useEffect(() => {
    if (active) setStep(0);
  }, [active]);

  const handleClose = () => {
    markTourSeen(tourId);
    onClose();
  };

  const handleNext = () => {
    if (step < steps.length - 1) {
      setStep(s => s + 1);
    } else {
      handleClose();
    }
  };

  const handlePrev = () => {
    if (step > 0) setStep(s => s - 1);
  };

  if (!active) return null;

  const spotlightPad = PAD;
  const hasRect = !!rect;
  const tooltipStyle = getTooltipStyle(rect, currentStep.placement);
  const arrowStyle = getArrowStyle(rect, currentStep.placement);
  const isAbove = currentStep.placement === 'top';

  const vw = window.innerWidth;

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 2000, pointerEvents: 'none' }}>

      {/* Dark overlay with spotlight cutout */}
      <svg
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'all' }}
        onClick={handleClose}
      >
        <defs>
          <mask id="spotlight-mask">
            <rect width="100%" height="100%" fill="white" />
            {hasRect && (
              <rect
                x={rect.left - spotlightPad}
                y={rect.top - spotlightPad}
                width={rect.width + spotlightPad * 2}
                height={rect.height + spotlightPad * 2}
                rx="10"
                fill="black"
              />
            )}
          </mask>
        </defs>
        <rect
          width="100%"
          height="100%"
          fill="rgba(0,0,0,0.55)"
          mask="url(#spotlight-mask)"
        />
      </svg>

      {/* Highlight border around target */}
      {hasRect && (
        <div style={{
          position: 'absolute',
          top: rect.top - spotlightPad,
          left: rect.left - spotlightPad,
          width: rect.width + spotlightPad * 2,
          height: rect.height + spotlightPad * 2,
          borderRadius: '10px',
          border: '2px solid rgba(99,102,241,0.8)',
          boxShadow: '0 0 0 2px rgba(99,102,241,0.25)',
          pointerEvents: 'none',
          animation: 'tourPulse 2s ease-in-out infinite',
        }} />
      )}

      {/* Tooltip */}
      <div
        style={{
          position: 'absolute',
          ...tooltipStyle,
          backgroundColor: t.surface,
          borderRadius: '14px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.22)',
          border: `1px solid ${t.border}`,
          padding: '18px 20px 16px',
          pointerEvents: 'all',
          maxWidth: vw < 400 ? `calc(${vw}px - 24px)` : '300px',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Arrow pointing to target */}
        {hasRect && arrowStyle && (
          <div style={{
            position: 'absolute',
            ...(isAbove ? { bottom: -7 } : { top: -7 }),
            ...arrowStyle,
            width: 14,
            height: 14,
            backgroundColor: t.surface,
            border: `1px solid ${t.border}`,
            transform: 'rotate(45deg)',
            borderRadius: '2px',
            ...(isAbove
              ? { borderTop: 'none', borderLeft: 'none' }
              : { borderBottom: 'none', borderRight: 'none' }),
          }} />
        )}

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '8px' }}>
          <span style={{ fontSize: '13px', fontWeight: '700', color: t.text }}>{currentStep.title}</span>
          <button
            onClick={handleClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: t.textMuted, padding: '0 0 0 8px', display: 'flex', flexShrink: 0 }}
          >
            <X size={14} />
          </button>
        </div>

        {/* Body */}
        <p style={{ fontSize: '13px', color: t.textSecondary, margin: '0 0 16px', lineHeight: '1.5' }}>
          {currentStep.body}
        </p>

        {/* Footer */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {/* Step dots */}
          <div style={{ display: 'flex', gap: '5px' }}>
            {steps.map((_, i) => (
              <div
                key={i}
                style={{
                  width: i === step ? 16 : 6,
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: i === step ? '#6366f1' : t.border,
                  transition: 'width 0.2s, background-color 0.2s',
                }}
              />
            ))}
          </div>

          {/* Nav buttons */}
          <div style={{ display: 'flex', gap: '8px' }}>
            {step > 0 && (
              <button
                onClick={handlePrev}
                style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '6px 12px', border: `1px solid ${t.border}`, borderRadius: '8px', backgroundColor: 'transparent', color: t.textSecondary, fontSize: '13px', cursor: 'pointer' }}
              >
                <ChevronLeft size={14} /> Back
              </button>
            )}
            <button
              onClick={handleNext}
              style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '6px 14px', border: 'none', borderRadius: '8px', backgroundColor: '#6366f1', color: 'white', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}
            >
              {step < steps.length - 1 ? <><span>Next</span><ChevronRight size={14} /></> : 'Done'}
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes tourPulse {
          0%, 100% { box-shadow: 0 0 0 2px rgba(99,102,241,0.25); }
          50% { box-shadow: 0 0 0 6px rgba(99,102,241,0.12); }
        }
      `}</style>
    </div>
  );
}

export default OnboardingTour;
